import { ActionTree, GetterTree, Module, MutationTree } from "vuex";
import { IEmploymentState } from "./types";
import { IIDAStore } from "../types";
import { endOfDay, startOfDay } from "date-fns";
import { Employment, CreateEmploymentWithWorkHourPoolsPostback } from "../../models/employments";
import { employmentsService } from "../../services/Employments.service";
import { StoreCache, storeCachePeriode } from "../storeCache";
import { areTimeperiodsOverlapping, IPeriode } from "../../utils/dateUtils";
import store from "@/store";
import { User } from "@/modules/security/models/user-model";

export const getInitialState = (): IEmploymentState => {
  return {
    searching: false,
    employments: [],
    storeCache: new StoreCache()
  };
};

const namespaced = true;

export const getters: GetterTree<IEmploymentState, IIDAStore> = {
  GetEmployments: (state) => (from: Date, to: Date) => {
    return state.employments.filter(
      (employment: Employment) =>
        employment.start_date <= to && endOfDay(employment.end_date) >= from
    );
  },
  GetEmploymentById: (state) => (employmentId: number) => {
    return state.employments.find((employment: Employment) => employment.id === employmentId);
  },
  GetEmploymentByEmployeeIdAndDate: (state) => (employeeId: number, date: Date) => {
    return state.employments.find(
      (employment: Employment) =>
        employment.employee_id === employeeId &&
        startOfDay(employment.start_date) <= startOfDay(date) &&
        date <= endOfDay(employment.end_date)
    );
  },
  GetEmploymentsByEmployeeId: (state) => (employeeId: number) => {
    return state.employments.filter(
      (employment: Employment) => employment.employee_id == employeeId
    );
  },
  GetEmploymentsByStartAndEndDate: (state) => (query: { startDate: Date; endDate: Date }) => {
    return state.employments.filter((e) =>
      areTimeperiodsOverlapping(
        { fra: startOfDay(e.start_date), tilOgMed: endOfDay(e.end_date) },
        {
          fra: startOfDay(query.startDate),
          tilOgMed: endOfDay(query.endDate)
        }
      )
    );
  }
};

export const actions: ActionTree<IEmploymentState, IIDAStore> = {
  reset({ commit }) {
    commit("reset");
  },
  fetchEmployments({ commit, state }, query: { from: Date; to: Date }): Promise<Employment[]> {
    return new Promise((resolve, reject) => {
      let from: Date = startOfDay(query.from);
      let to: Date = endOfDay(query.to);
      const user: User = store.getters["security/getUser"]();
      if (user.isEmployee()) {
        from = user.employmentsFrom();
        to = user.employmentsTo(to);
      }
      const periode = { fra: from, tilOgMed: to } as IPeriode;
      if (!state.storeCache.isCached(periode)) {
        try {
          employmentsService.fetchEmployments(from, to).then((employments) => {
            commit("updateEmployments", { employments, from, to });
            return resolve(employments);
          });
        } catch (error) {
          return reject(error);
        }
      } else {
        const employments = state.employments.filter(
          (ab) => endOfDay(ab.end_date) >= from && ab.start_date < to
        );
        return resolve(employments);
      }
    });
  },
  createEmployment({ commit }, employment: Employment): Promise<Employment> {
    return new Promise((resolve, reject) => {
      employmentsService
        .createEmployment(employment)
        .then((newEmployment) => {
          commit("insertEmployment", newEmployment);
          resolve(newEmployment);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  createEmploymentWithWorkHourPools({ commit }, query: {employment: Employment, copyWorkHourPoolsFrom: number}): Promise<CreateEmploymentWithWorkHourPoolsPostback> {
    return new Promise((resolve, reject) => {
      employmentsService
        .createEmploymentWithWorkHourPools(query.employment, query.copyWorkHourPoolsFrom)
        .then((employmentPostback) => {
          commit("insertEmployment", employmentPostback.employment);

          resolve(employmentPostback);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  updateEmployment({ commit }, employment: Employment): Promise<Employment> {
    return new Promise((resolve, reject) => {
      employmentsService
        .updateEmployment(employment)
        .then((updatedEmployment) => {
          commit("updateEmployment", updatedEmployment);
          resolve(updatedEmployment);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  updateEmploymentSoCalculatesAs({ commit }, employment: Employment): Promise<Employment> {
    return new Promise((resolve, reject) => {
      employmentsService
        .updateEmploymentSoCalculatesAs(employment)
        .then((updatedEmployment) => {
          commit("updateEmployment", updatedEmployment);
          resolve(updatedEmployment);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  deleteEmployment({ commit }, employmentId: number): Promise<any> {
    return new Promise((resolve, reject) => {
      employmentsService
        .deleteEmployment(employmentId)
        .then((response) => {
          commit("deleteEmployment", employmentId);
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
};

export const mutations: MutationTree<IEmploymentState> = {
  reset(state: IEmploymentState) {
    Object.assign(state, getInitialState());
  },
  insertEmployment(state: IEmploymentState, employment: Employment) {
    state.employments.push(employment);
  },
  updateEmployment(state: IEmploymentState, employment: Employment) {
    const index = state.employments.findIndex((d) => d.id === employment.id);
    state.employments.splice(index, 1);
    state.employments.push(employment);
  },
  deleteEmployment(state: IEmploymentState, employmentId: number) {
    const index = state.employments.findIndex((d) => d.id === employmentId);
    state.employments.splice(index, 1);
  },
  updateEmployments(
    state: IEmploymentState,
    payload: { employments: Employment[]; from: Date; to: Date }
  ) {
    state.employments = [
      ...state.employments,
      ...payload.employments.filter(
        (newEmployments) => !state.employments.find((existing) => existing.equals(newEmployments))
      )
    ];
    if (payload.from && payload.to) {
      state.storeCache.addCache(new storeCachePeriode({ fra: payload.from, tilOgMed: payload.to }));
    }
  }
};

export const employmentsStoreModule: Module<IEmploymentState, IIDAStore> = {
  namespaced: namespaced,
  state: getInitialState(),
  getters: getters,
  actions: actions,
  mutations: mutations
};
